import {l} from "types/typescriptdefinisjoner_lederplan";
import _ from "lodash";
import moment from "moment";
import {regNavn} from "../tredjepartsTjenestenavn";
import {IIntervalService} from "angular";
import {BeregnetMøtevarighet, MøteRessurs} from "../ressurser/MøteRessurs";
import {SakRessurs, Sakstidspunkt} from "../ressurser/SakRessurs";
import {erNill} from "felles/stoettefunk/nillsjekk";
import {Møtedatoer} from "felles/tjenester/møte/Møtedatoer";

require('twix'); //utvider moment

export class Møteprogresjonberegning {

    static regNavn = "Møteprogresjonberegning";

    static forInjisering() {
        return [regNavn.ng.$interval, Møteprogresjonberegning]
    }

    constructor(private $interval: IIntervalService) {
    }

    møteprogresjon: Møteprogresjon;

    private møte: l.MøteModell;
    private timer;

    //offentlige metoder

    initialiserOgstartTimer(møte: l.MøteModell): Møteprogresjon {
        //console.log("Møteprogresjonberegning initialiserOgstartTimer")
        this.reset();

        this.møte = møte;
        this.møteprogresjon = new Møteprogresjon();
        this.oppdaterProgresjonsindikator();

        //oppdater møteprogresjonsindikator hvert 30.sekund
        this.timer = this.$interval(() => this.oppdaterProgresjonsindikator(), 1000 * 30);

        return this.møteprogresjon;
    }

    stopTimer(møte: l.MøteModell) {
        //vi må håndtere at ut-av-rekkefølge kall til initialiserOgstartTimer(..) og stopTimer(..)
        //feks opptrer sekvensen initialiserOgstartTimer(møte_A) stopTimer(møte_B) når man i appen blar mellom møter
        //pga rekkefølgen på $ionicView eventer (leav eventer kan komme etter enter eventer)
        if (this.møte && this.møte.id !== møte.id) return;

        //console.log("Møteprogresjonberegning stopTimer")
        this.reset();
    }

    //hjelpemetoder

    //offentlig for testbarhet
    public reset() {
        this.$interval.cancel(this.timer);
        this.møte = this.møteprogresjon = this.timer = null;
    }

    private oppdaterProgresjonsindikator() {
        if (this.ikkeVisProgresjonsindikator()) {
            this.møteprogresjon.visProgresjonsindikator = false;
            return;
        }

        this.møteprogresjon.visProgresjonsindikator = true;
        this.initialiserProgresjonsindikatorIntervaller();
        var nå = moment();
        this.møteprogresjon.moetettidBrukt = moment(this.møte.dato.start).twix(nå.toDate()).length("minutes");
        this.møteprogresjon.moetettidRest = this.møteprogresjon.varighet.justertVarighet - this.møteprogresjon.moetettidBrukt;
        this.møteprogresjon.moeteprogresjonIProsent = (this.møteprogresjon.moetettidBrukt / (this.møteprogresjon.moetettidBrukt + this.møteprogresjon.moetettidRest)) * 100;
        this.møte.saker.forEach(sak => {
            var progresjon = this.møteprogresjon.saksprogresjonIProsent[sak.id];
            if (erNill(progresjon)){
                // vi har fått mange feil om undefined, men har ikke klart å skjønne hvorfor den kommer.
                // siden denne koden kjører hvert 30 sekund blir det veldig mange feilmeldinger - så vi bare dropper å gjøre noe for denne
               return;
            }
            progresjon.erUnderBehandling = false;
            if (nå.isAfter(progresjon.slutt)) {
                progresjon.progresjon = 100;
                progresjon.rest = 0;
            } else if (nå.isBefore(progresjon.start)) {
                progresjon.progresjon = 0;
            } else {
                progresjon.erUnderBehandling = true;
                progresjon.progresjon = ((nå.toDate().getTime() - progresjon.start.getTime()) / (progresjon.slutt.getTime() - progresjon.start.getTime())) * 100;
                progresjon.rest = Math.round(progresjon.varighet * (1 - (progresjon.progresjon / 100)));
            }
        });
        //console.log("oppdaterProgresjonsindikator", this.møteprogresjon)
    }

    /**
     * Møte kun på 1 dag: Vis progresjon dersom vi er inne en time før/etter møte, OG minst en av sakene har varighet satt
     * Møte over flere dager: Ikke vis progresjonsberegning.
     * */
    private ikkeVisProgresjonsindikator() {
        // For å kunne vise denne må vi utøke med å håndtere hvilken dag vi er på i møtet
        if(Møtedatoer.erFlerdagsmøte(this.møte.dato)) return true;

        // Når møtet er på en enkelt dag
        if (moment(this.møte.dato.start).subtract(1, "hour").isAfter()) return true;
        if (moment(this.møte.dato.slutt).add(1, "hour").isBefore()) return true;
        if (this.møte.saker.every(sak => _.isNil(sak.varighet))) return true;

        return false;
    }

    private initialiserProgresjonsindikatorIntervaller() {
        if (this.møteprogresjon.progresjonsindikatorIntervaller) return;

        const sakstidspunkter = SakRessurs.sakstidspunkter(this.møte);
        this.møteprogresjon.saksprogresjonIProsent = _(sakstidspunkter).mapValues((sakstidspunkt: Sakstidspunkt) => {
            return {
                sakId: sakstidspunkt.sakId,
                erUnderBehandling: false,
                progresjon: 0,
                varighet: sakstidspunkt.varighet,
                start: sakstidspunkt.start,
                slutt: sakstidspunkt.slutt,
                rest: sakstidspunkt.varighet
            };
        }).value();
        this.møteprogresjon.varighet = MøteRessurs.beregnetMøtevarighet(this.møte);

        this.møteprogresjon.progresjonsindikatorIntervaller = _(sakstidspunkter).values().map(t => t.varighet).value();
        if (this.møteprogresjon.varighet.differanse < 0)
            this.møteprogresjon.progresjonsindikatorIntervaller.push(Math.abs(this.møteprogresjon.varighet.differanse));

        //console.log("beregnProgresjonsindikatorIntervaller", this.møteprogresjon)
    }
}

export class Møteprogresjon {
    varighet: BeregnetMøtevarighet;
    visProgresjonsindikator = false;
    progresjonsindikatorIntervaller: number[] = null;
    /** minutter siden møte.start */
    moetettidBrukt: number = 0;
    /** minutter igjen til varighet.justertSlutt */
    moetettidRest: number;
    /** prosent av varighet.justertVarighet benyttet, mellom 0 og 100. */
    moeteprogresjonIProsent: number = 0;
    saksprogresjonIProsent: { [sakId: string]: Saksprogresjon; } = {};
}

export interface Saksprogresjon {
    sakId: string;
    /** sann dersom sakens start er passert men ikke slutt passert */
    erUnderBehandling: boolean;
    /** tid igjen av saken, i prosent (0-100) */
    progresjon: number;
    /** sakens beregnede varighet i minutter */
    varighet: number;
    start: Date;
    slutt: Date;
    /** minuttes igjen av saken */
    rest: number
}

